import { useEffect, useMemo } from 'react';

import { debounce, throttle, type ThrottleOptions } from 'utils/helpers';

import { useLatest } from './useLatest';

interface Options extends ThrottleOptions {
	dependencies?: unknown[];
}

/** Create a rate limited function. */
// oxlint-disable-next-line max-params
function useRateLimitedCallback<Args extends unknown[], Ret>(
	callback: (...args: Args) => Ret,
	delay: number,
	rateLimiter: typeof debounce | typeof throttle,
	{ dependencies = [], leading, trailing }: Options = {},
) {
	const savedCallback = useLatest(callback);

	const limitedCallback = useMemo(
		() =>
			rateLimiter((...args: Args) => savedCallback.current?.(...args), delay, {
				leading,
				trailing,
			}),
		// oxlint-disable-next-line react-hooks/exhaustive-deps
		[delay, ...dependencies],
	);

	// On unmount or if the rete limiting changes.
	useEffect(
		() => () => {
			limitedCallback.flush();
		},
		[limitedCallback],
	);

	return limitedCallback;
}

/**
 * Run an function in throttled intervals.
 *
 * @example
 *
 * function Component() {
 *   const [
 *     handleThingThrottled,
 *     flushHandleThing,
 *     cancelHandleThing,
 *   ] = useThrottledCallback(
 *     () => {
 *       // Do something
 *     },
 *     100,
 *   );
 *   useEffect(() => {
 *     // Some value changed and the queued, throttled call is no longer
 *     // relevant or any previous calls should be cleared.
 *     // flushHandleThing();
 *     // cancelHandleThing();
 *   }, [someValue])
 *
 *   return <OtherComponent value={someValue} onThing={handleThingThrottled} />;
 * }
 */
export function useThrottledCallback<Args extends unknown[], Ret>(
	callback: (...args: Args) => Ret,
	delay: number,
	{ dependencies = [], leading, trailing }: Options = {},
) {
	const throttled = useRateLimitedCallback(callback, delay, throttle, {
		dependencies,
		leading,
		trailing,
	});
	// oxlint-disable-next-line typescript/unbound-method
	return [throttled, throttled.flush, throttled.cancel] as const;
}

/**
 * Run an function after a debounce delay.
 *
 * @example
 *
 * function Component() {
 *   const [
 *     handleThingDebounced,
 *     flushHandleThing,
 *     cancelHandleThing,
 *   ] = useDebouncedCallback(
 *     () => {
 *       // Do something
 *     },
 *     200,
 *   );
 *   useEffect(() => {
 *     // Some value changed and the queued, debounced call is no longer
 *     // relevant or any previous calls should be cleared.
 *     // flushHandleThing();
 *     // cancelHandleThing();
 *   }, [someValue])
 *
 *   return <OtherComponent value={someValue} onThing={handleThingDebounced} />;
 * }
 */
export function useDebouncedCallback<Args extends unknown[], Ret>(
	callback: (...args: Args) => Ret,
	delay: number,
	{ dependencies = [], leading, trailing }: Options = {},
) {
	const debounced = useRateLimitedCallback(callback, delay, debounce, {
		dependencies,
		leading,
		trailing,
	});
	// oxlint-disable-next-line typescript/unbound-method
	return [debounced, debounced.flush, debounced.cancel] as const;
}
